import { api, priceApi } from "@/utils/api"
import { LOCALE_VI } from "@/utils/constants"

import { formErrorMessage, HttpMethod, wrapperApiRequest } from "@/apis/baseRequest"
import { BlockchainNetwork } from "@renec-foundation/relend-adapter"

export interface SubscribeRelendPayload {
  email_address: string
  wallet_address: string
  is_liquidator: boolean
}

// Generic API request function
const makeApiRequest = wrapperApiRequest(api)

export const subscribeRelendRequest = (payload: SubscribeRelendPayload) =>
  makeApiRequest(HttpMethod.POST, "/lending_subscribers", payload)

export const fetchBanners = (lang: string) =>
  makeApiRequest(HttpMethod.GET, "/lending_banners", {}, { is_en: lang !== LOCALE_VI })

export const fetchAndRegisterBTEReward = (walletAddress: string, obligationAddress: string) =>
  makeApiRequest(HttpMethod.GET, `/lendings/rewards/${walletAddress}/${obligationAddress}`)

export const refreshBTEReward = (walletAddress: string, obligationAddress: string) =>
  makeApiRequest(HttpMethod.POST, "/lendings/rewards/refresh", {
    wallet_address: walletAddress,
    obligation_address: obligationAddress,
  })

export const submitClaimTx = (walletAddress: string, obligationAddress: string, txHash: string) =>
  makeApiRequest(HttpMethod.POST, "/lendings/rewards/transactions", {
    wallet_address: walletAddress,
    obligation_address: obligationAddress,
    tx_hash: txHash,
  })

export const fetchReferralInfo = (network: BlockchainNetwork, walletAddress?: string) =>
  walletAddress ? makeApiRequest(HttpMethod.GET, `/lending/referral/${walletAddress}`, {}, { network }) : null

export const registerReferrer = (network: BlockchainNetwork, referralCode: string, walletAddress?: string) =>
  walletAddress
    ? makeApiRequest(HttpMethod.POST, "/lending/referral/register", {
        referral_code: referralCode,
        wallet_address: walletAddress,
        network,
      })
    : null

export const fetchReferralHistories = (
  network: BlockchainNetwork,
  walletAddress?: string,
  page?: number,
  per_page?: number,
) =>
  walletAddress
    ? makeApiRequest(HttpMethod.GET, `/lending/referral/${walletAddress}/history`, {}, { page, per_page, network })
    : null

export const fetchReferralOverview = (network: BlockchainNetwork, walletAddress?: string) =>
  walletAddress ? makeApiRequest(HttpMethod.GET, `/lending/referral/${walletAddress}/rewards`, {}, { network }) : null

export const fetchSupplyToEarnAPY = (network: BlockchainNetwork) =>
  makeApiRequest(HttpMethod.GET, `/lending/supply_to_earn/apy?network=${network}`)

export const fetchSupplyToEarnReward = (
  network: BlockchainNetwork,
  walletAddress?: string,
  obligationAddress?: string,
) =>
  walletAddress && obligationAddress
    ? makeApiRequest(
        HttpMethod.GET,
        `/lending/supply_to_earn/reward/${walletAddress}/${obligationAddress}?network=${network}`,
      )
    : null

export const fetchLiquidations = (page?: number, per_page?: number) =>
  makeApiRequest(HttpMethod.GET, `/lending/liquidation`, {}, { page, per_page })

export const fetchPrices = async () => {
  try {
    const response = await priceApi.get("/")
    return response.data
  } catch (error: any) {
    const errorMessage = formErrorMessage(error)
    return { error: errorMessage }
  }
}

export const fetchPriceChange = (walletAddress?: string, tx = "") =>
  walletAddress
    ? makeApiRequest(HttpMethod.GET, `/lending/price_change/wallet/${walletAddress}/refresh?tx=${tx ?? ""}`)
    : null

export const fetchObligationProxyHistory = (walletAddress: string, obligationAddress: string) =>
  walletAddress ? makeApiRequest(HttpMethod.GET, `/lending/proxy/history/${walletAddress}/${obligationAddress}`) : null
